import React, { useEffect, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { ThumbsUp, ThumbsDown, Star } from "@phosphor-icons/react";

/** RatingWidget — appears on a paid task that the client hasn't yet rated.
 *  Auto-triggers if the URL has `?rate=up` or `?rate=down`. */
export default function RatingWidget({ task, onRated }) {
    const [mode, setMode] = useState(null); // null | "up" | "down"
    const [comment, setComment] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [done, setDone] = useState(null); // { rating, google_review_url }

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const r = params.get("rate");
        if (r === "up" || r === "down") {
            setMode(r);
            // Clean the URL
            params.delete("rate");
            window.history.replaceState({}, "", window.location.pathname + (params.toString() ? `?${params}` : ""));
        }
    }, []);

    async function submit(rating) {
        setBusy(true); setErr("");
        try {
            const { data } = await http.post(`/tasks/${task.id}/rate`, {
                rating,
                comment: comment.trim(),
            });
            setDone(data);
            if (onRated) onRated();
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    // Already rated server-side → show thank-you summary instead of widget
    if (task.client_rating) {
        return (
            <div data-testid="rating-already" className="mt-8 rounded-sm border border-white/10 bg-slate-900/40 p-5 flex items-center gap-3 text-sm text-slate-300">
                {task.client_rating === "up" ? (
                    <ThumbsUp size={22} weight="fill" className="text-gold" />
                ) : (
                    <ThumbsDown size={22} weight="fill" className="text-slate-400" />
                )}
                <span>You rated this job — thanks for the feedback.</span>
            </div>
        );
    }

    // After submission UI
    if (done) {
        return (
            <div data-testid="rating-thanks" className="mt-8 rounded-sm border border-gold/30 bg-gradient-to-br from-gold/10 to-transparent p-6">
                {done.rating === "up" ? (
                    <>
                        <div className="flex items-center gap-2 text-gold font-serif text-2xl">
                            <Star size={24} weight="fill" /> Thank you!
                        </div>
                        <p className="text-slate-300 mt-2 text-sm">
                            Your contractor's Quality Score just got a boost. Would you take 30 more seconds to leave a public Google review? It really helps small contractors get more work.
                        </p>
                        {done.google_review_url && (
                            <a
                                data-testid="google-review-link"
                                href={done.google_review_url}
                                target="_blank"
                                rel="noreferrer"
                                className="btn-gold inline-block mt-4 text-sm"
                            >
                                Leave a Google review →
                            </a>
                        )}
                    </>
                ) : (
                    <>
                        <div className="flex items-center gap-2 text-slate-200 font-serif text-2xl">
                            <ThumbsDown size={22} weight="fill" className="text-slate-400" /> We're on it.
                        </div>
                        <p className="text-slate-300 mt-2 text-sm">
                            Thank you for telling us — a Butler will reach out within one business day to make it right.
                        </p>
                    </>
                )}
            </div>
        );
    }

    return (
        <div data-testid="rating-widget" className="mt-8 rounded-sm border border-white/10 bg-slate-900/60 p-6">
            <div className="label-eyebrow text-gold mb-2">/ How did it go?</div>
            <h3 className="font-serif text-2xl mb-4">Rate your Butler Room experience</h3>

            {!mode ? (
                <div className="flex gap-3 flex-wrap">
                    <button
                        data-testid="rate-up"
                        onClick={() => setMode("up")}
                        disabled={busy}
                        className="btn-gold inline-flex items-center gap-2 text-sm px-5 py-2.5 disabled:opacity-50"
                    >
                        <ThumbsUp size={16} weight="fill" /> Loved it
                    </button>
                    <button
                        data-testid="rate-down"
                        onClick={() => setMode("down")}
                        disabled={busy}
                        className="inline-flex items-center gap-2 text-sm px-5 py-2.5 rounded-sm border border-white/15 bg-white/5 text-slate-200 hover:bg-white/10 disabled:opacity-50"
                    >
                        <ThumbsDown size={16} weight="fill" /> Not satisfied
                    </button>
                </div>
            ) : (
                <div>
                    <div className="text-sm text-slate-300 mb-3">
                        {mode === "up" ? "Anything you want to say about the contractor? (Optional, will be shown on their profile.)" : "What went wrong? (We'll reach out to make it right.)"}
                    </div>
                    <textarea
                        data-testid="rating-comment"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={3}
                        maxLength={1000}
                        placeholder={mode === "up" ? "e.g. Showed up on time, careful work, fair price." : "Tell us what happened — we'll investigate."}
                        className="w-full bg-slate-950 border border-white/10 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-gold"
                    />
                    {err && <div className="text-red-400 text-xs mt-2">{err}</div>}
                    <div className="mt-3 flex gap-2 flex-wrap">
                        <button
                            data-testid="rating-submit"
                            onClick={() => submit(mode)}
                            disabled={busy}
                            className="btn-gold text-sm px-5 py-2 disabled:opacity-50"
                        >
                            {busy ? "Sending…" : "Submit"}
                        </button>
                        <button
                            data-testid="rating-cancel"
                            onClick={() => { setMode(null); setComment(""); setErr(""); }}
                            disabled={busy}
                            className="text-sm px-5 py-2 rounded-sm border border-white/15 bg-white/5 text-slate-300 hover:bg-white/10 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
